import apiClient from './api';

/**
 * Facebook tracking service — best-practice "redundant" setup:
 *
 *   Browser Pixel (fbq)  +  Server Conversion API (CAPI proxy)
 *
 * Every event is sent BOTH ways sharing the same `event_id`, so Meta
 * deduplicates them. This maximises Event Match Quality and coverage while
 * staying resilient to ad-blockers / iOS (the server side always fires).
 *
 * - The access TOKEN lives ONLY on the backend (appsettings.json).
 * - The PIXEL ID is needed in the browser to load the pixel; it is NOT secret
 *   (it is visible in any pixel) and is read from REACT_APP_FB_PIXEL_ID.
 * - The backend injects client_ip_address + client_user_agent server-side.
 *
 * All methods are fire-and-forget: failures never block the checkout flow.
 */

const ENDPOINT = '/FacebookConversion/event';
// Pixel ID from env; falls back to the production pixel if the env var is unset.
const PIXEL_ID = process.env.REACT_APP_FB_PIXEL_ID || '627775570354762';
const STORAGE_KEY = 'fb_customer';

let storedCustomer = (() => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}') || {};
  } catch {
    return {};
  }
})();

/* ----------------------------- helpers ----------------------------------- */

function genEventId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function getCookie(name) {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1')}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : undefined;
}

function setCookie(name, value, days = 90) {
  const maxAge = days * 24 * 60 * 60;
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; SameSite=Lax`;
}

// SHA-256 (lower-cased, trimmed) as required by the Conversion API.
async function hash(value) {
  if (value === undefined || value === null) return undefined;
  const normalized = String(value).trim().toLowerCase();
  if (!normalized) return undefined;
  try {
    const buffer = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(normalized));
    return Array.from(new Uint8Array(buffer)).map(b => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return undefined;
  }
}

// Tunisian phone normalisation: 8 local digits -> 216XXXXXXXX (E.164 without +).
function normalizePhone(phone) {
  if (!phone) return undefined;
  let digits = String(phone).replace(/\D/g, '');
  if (digits.length === 8) digits = `216${digits}`;
  return digits || undefined;
}

/* ------------------------- browser pixel init ---------------------------- */

// Capture fbclid -> _fbc cookie so CAPI always has the click id, even before
// the pixel script finishes loading.
function captureFbc() {
  try {
    const params = new URLSearchParams(window.location.search);
    const fbclid = params.get('fbclid');
    if (fbclid && !getCookie('_fbc')) {
      setCookie('_fbc', `fb.1.${Date.now()}.${fbclid}`);
    }
  } catch {
    /* ignore */
  }
}

function loadPixel() {
  if (!PIXEL_ID || typeof window === 'undefined' || window.fbq) return;
  /* eslint-disable */
  !function (f, b, e, v, n, t, s) {
    if (f.fbq) return; n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments) };
    if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = '2.0'; n.queue = [];
    t = b.createElement(e); t.async = !0; t.src = v; s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
  }(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */
  window.fbq('init', PIXEL_ID);
  window.fbq('track', 'PageView', {}, { eventID: genEventId() });
}

// Pixels already initialised in this page (env pixel + any marque pixels).
const initializedPixels = new Set(PIXEL_ID ? [PIXEL_ID] : []);

// Initialise an extra pixel (e.g. the pixel of the product's marque) once.
function initPixel(pixelId) {
  if (!pixelId || typeof window === 'undefined' || !window.fbq) return;
  const pid = String(pixelId).trim();
  if (!pid || initializedPixels.has(pid)) return;
  try {
    window.fbq('init', pid);
    window.fbq('trackSingle', pid, 'PageView', {}, { eventID: genEventId() });
    initializedPixels.add(pid);
  } catch {
    /* ignore */
  }
}

// Self-initialise on import.
if (typeof window !== 'undefined') {
  captureFbc();
  loadPixel();
}

function pixelTrack(eventName, customData, eventId, pixelId) {
  try {
    if (typeof window === 'undefined' || !window.fbq) return;
    const pid = pixelId ? String(pixelId).trim() : '';
    if (pid) {
      // Fire ONLY on the marque's pixel so events land in the right account.
      initPixel(pid);
      window.fbq('trackSingle', pid, eventName, customData, { eventID: eventId });
    } else if (PIXEL_ID) {
      window.fbq('trackSingle', PIXEL_ID, eventName, customData, { eventID: eventId });
    }
  } catch {
    /* ignore */
  }
}

/* --------------------------- advanced matching --------------------------- */

/**
 * Persist known identity fields (phone/email/firstName/lastName/city/country)
 * so every subsequent event carries hashed user_data. Also feeds the browser
 * pixel's advanced matching.
 */
function setCustomer(customer = {}) {
  storedCustomer = { ...storedCustomer };
  Object.entries(customer).forEach(([key, val]) => {
    if (val !== undefined && val !== null && val !== '') {
      storedCustomer[key] = val;
    }
  });
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(storedCustomer));
  } catch {
    /* ignore quota / private-mode errors */
  }
  // Re-init the browser pixel with RAW advanced-matching data (the pixel hashes
  // it itself). Improves browser-side match quality.
  try {
    if (PIXEL_ID && typeof window !== 'undefined' && window.fbq) {
      const am = {};
      if (storedCustomer.email) am.em = String(storedCustomer.email).trim().toLowerCase();
      const ph = normalizePhone(storedCustomer.phone);
      if (ph) am.ph = ph;
      if (storedCustomer.firstName) am.fn = String(storedCustomer.firstName).trim().toLowerCase();
      if (storedCustomer.lastName) am.ln = String(storedCustomer.lastName).trim().toLowerCase();
      if (storedCustomer.city) am.ct = String(storedCustomer.city).trim().toLowerCase();
      if (storedCustomer.country) am.country = String(storedCustomer.country).trim().toLowerCase();
      if (Object.keys(am).length > 0) window.fbq('init', PIXEL_ID, am);
    }
  } catch {
    /* ignore */
  }
}

async function buildUserData(extra = {}) {
  const c = { ...storedCustomer, ...extra };
  const userData = {};

  const em = await hash(c.email);
  const ph = await hash(normalizePhone(c.phone));
  const fn = await hash(c.firstName);
  const ln = await hash(c.lastName);
  const ct = await hash(c.city);
  const country = await hash(c.country || 'TN');

  if (em) userData.em = [em];
  if (ph) userData.ph = [ph];
  if (fn) userData.fn = [fn];
  if (ln) userData.ln = [ln];
  if (ct) userData.ct = [ct];
  if (country) userData.country = [country];

  const fbp = getCookie('_fbp');
  const fbc = getCookie('_fbc');
  if (fbp) userData.fbp = fbp;
  if (fbc) userData.fbc = fbc;

  return userData;
}

function buildCustomData(data = {}) {
  const customData = {};
  if (data.currency) customData.currency = data.currency;
  if (data.value !== undefined && data.value !== null) customData.value = Number(data.value);
  if (data.productId !== undefined && data.productId !== null) {
    customData.content_ids = [String(data.productId)];
    customData.content_type = data.contentType || 'product';
  }
  if (data.productName) customData.content_name = data.productName;
  if (Array.isArray(data.items)) {
    customData.contents = data.items.map(it => ({
      id: String(it.productId ?? it.id),
      quantity: it.quantity ?? 1,
      item_price: it.price,
    }));
    if (!customData.content_ids) {
      customData.content_ids = data.items.map(it => String(it.productId ?? it.id));
      customData.content_type = 'product';
    }
  }
  if (data.orderId !== undefined && data.orderId !== null) {
    customData.order_id = String(data.orderId);
  }
  return customData;
}

/* ------------------------------- send ------------------------------------ */

async function track(eventName, data = {}, customer = {}) {
  const eventId = data.eventId || genEventId();
  const customData = buildCustomData(data);

  // 1) Browser pixel (shared event_id for dedup). Uses the marque's pixel when provided.
  pixelTrack(eventName, customData, eventId, data.pixelId);

  // 2) Server CAPI (authoritative, ad-blocker proof).
  try {
    const userData = await buildUserData(data.customer || customer);
    const event = {
      event_name: eventName,
      event_id: eventId,
      event_time: Math.floor(Date.now() / 1000),
      action_source: 'website',
      event_source_url: typeof window !== 'undefined' ? window.location.href : undefined,
      user_data: userData,
      custom_data: customData,
    };
    // productId lets the backend resolve the pixelId/token from the product's marque.
    const productId = data.productId ?? (Array.isArray(data.items) ? data.items[0]?.productId ?? data.items[0]?.id : undefined);
    return await apiClient.post(ENDPOINT, {
      data: [event],
      productId: productId !== undefined && productId !== null ? Number(productId) : undefined,
    });
  } catch {
    // Never let tracking break the UX.
    return undefined;
  }
}

const facebookConversionService = {
  setCustomer,
  initPixel,
  trackViewContent: (data, customer) => track('ViewContent', data, customer),
  trackInitiateCheckout: (data, customer) => track('InitiateCheckout', data, customer),
  trackLead: (data, customer) => track('Lead', data, customer),
  trackPurchase: (data, customer) => {
    // Never send the same purchase twice from this browser (re-submit, retry…).
    const orderId = data?.orderId;
    if (orderId !== undefined && orderId !== null) {
      const key = `fb_purchase_${orderId}`;
      try {
        if (localStorage.getItem(key)) return Promise.resolve(undefined);
        localStorage.setItem(key, '1');
      } catch { /* ignore */ }
    }
    return track('Purchase', data, data?.customer || customer);
  },
};

export default facebookConversionService;
